import { useEffect, useState } from 'react';

import { Box, Typography } from '@mui/material';

/** How long each line stays before the next takes its place. */
export const LINE_MS = 2200;

/** On the page for a screen reader, off it for everyone else. */
const visuallyHidden = {
  position: 'absolute',
  // In px, not 1: MUI reads a width or height of 1 as 100%, which made this
  // as tall as the whole chat and scrolled the conversation out of view.
  width: '1px',
  height: '1px',
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const;

/**
 * What is happening while what they said is being read, in the chat where
 * Keni's reply will land: a line, not a bubble, that changes as the reading goes, with the
 * dots after it moving so an unchanged line still says it is working.
 *
 * In place of the typing dots, for a wait long enough to say what is happening.
 * Said once to a screen reader; the changing lines are for the eye. Still for
 * anyone who has asked for less motion.
 */
const ReadingBubble = ({ lines }: { lines: string[] }) => {
  const [at, setAt] = useState(0);
  useEffect(() => {
    const id = window.setInterval(
      () => setAt((i) => (i + 1) % lines.length),
      LINE_MS,
    );
    return () => window.clearInterval(id);
  }, [lines.length]);

  return (
    // No bubble: a status line on the chat itself, on Keni's side, so it
    // reads as something happening rather than something said.
    <Box sx={{ alignSelf: 'flex-start', maxWidth: '82%' }}>
      <Box sx={visuallyHidden} role='status'>
        {lines[0]}
      </Box>
      <Typography
        key={at}
        aria-hidden
        // Italic and grey, so it reads as a status passing through rather
        // than something Keni said that will stay. Neutral 600 rather than
        // text.secondary, which is 3.3:1 here and fails; this is 6.1:1.
        sx={{
          color: 'custom.neutral.600',
          fontStyle: 'italic',
          animation: 'readingIn .4s ease both',
          '@keyframes readingIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
        variant='body1'
      >
        {lines[at]}
        {[0, 1, 2].map((i) => (
          <Box
            key={i}
            component='span'
            sx={{
              animation: 'readingDot 1.2s ease-in-out infinite',
              animationDelay: `${i * 0.2}s`,
              '@keyframes readingDot': {
                '0%, 60%, 100%': { opacity: 0.2 },
                '30%': { opacity: 1 },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            .
          </Box>
        ))}
      </Typography>
    </Box>
  );
};

ReadingBubble.displayName = 'ReadingBubble';
export default ReadingBubble;
